import React, { useMemo, useCallback } from 'react';
import type { ScannerData } from '../types/ScannerData';
import type { DataMode } from '../types/DataMode';
import { getExpiryFromRecord } from '../utils/filterUtils';
import { columnKeyToHistoricalSource } from '../utils/dataTableHistoricalSource';
import {
  computeEffectiveBidAskVolumes,
  effectiveOptionPriceForPremium,
  parseOptionSide,
} from '../utils/legacyVolumeStrikeAggregation';
import type { ContractHistoricalParams } from './ContractHistoricalModal';
import { getSimpleTableDensityMetrics, type SimpleScannerTableDensity } from '../utils/simpleScannerTableDensity';

export type ConsolidatedTableMode = 'stable' | 'rtl_legacy';

/** How rows are rolled up before net metrics are computed. */
export type ConsolidatedGroupBy = 'strike_expiry' | 'strike';

export interface StrikeAggRow {
  underlying: string;
  expiry: string;
  /** When rolled up across expiries: human-readable expiry summary (e.g. "3 exp: 2025-06-01, …"). */
  expiryDetail: string;
  strike: number;
  netDelta: number;
  netGamma: number;
  netVanna: number;
  netCharm: number;
  netOI: number;
  callOI: number;
  putOI: number;
  pctFromSpot: number;
  netVolume: number;
  netTrades: number;
  sampleRow: ScannerData;
  sampleCallRow: ScannerData | null;
  samplePutRow: ScannerData | null;
}

function num(r: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Stable Theta rows may carry vanna/charm under nested buckets (e.g. second_order → wrapper → vanna). */
function numGreekFromRowOrNested(r: Record<string, unknown>, ...keys: string[]): number {
  let v = num(r, ...keys);
  if (v !== 0) return v;
  const walk = (obj: Record<string, unknown>, depth: number): number => {
    if (depth > 8) return 0;
    const direct = num(obj, ...keys);
    if (direct !== 0) return direct;
    for (const child of Object.values(obj)) {
      if (child && typeof child === 'object' && !Array.isArray(child)) {
        const inner = walk(child as Record<string, unknown>, depth + 1);
        if (inner !== 0) return inner;
      }
    }
    return 0;
  };
  const nests = [
    'second_order',
    'third_order',
    'greeks',
    'first_order',
    'data',
    'response',
    'secondOrder',
    'thirdOrder',
    'firstOrder',
  ] as const;
  for (const nest of nests) {
    const blob = r[nest];
    if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
      v = walk(blob as Record<string, unknown>, 0);
      if (v !== 0) return v;
    }
  }
  // Last resort: depth-first anywhere on the row (Theta shapes vary by endpoint/version).
  v = walk(r, 0);
  return v !== 0 ? v : 0;
}

function optionSide(r: ScannerData): 'C' | 'P' {
  const t = String(r['Option Type'] ?? r.optionType ?? r.option_type ?? '').toUpperCase();
  if (t === 'P' || t === 'PUT') return 'P';
  return 'C';
}

/** Normalize any expiry shape to YYYY-MM-DD for grouping (fixes C/P split when formats differ). */
export function normalizeConsolidatedExpiryKey(row: ScannerData): string {
  const raw = getExpiryFromRecord(row) || row['Expiry Date'] || '';
  if (!raw) return '';
  const fromFmt = getExpiryFromRecord({ 'Expiry Date': raw });
  if (fromFmt && /^\d{4}-\d{2}-\d{2}/.test(fromFmt)) return fromFmt.slice(0, 10);
  const s = typeof raw === 'string' ? raw : raw && (raw as Date).toISOString ? (raw as Date).toISOString().slice(0, 10) : String(raw);
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 8) {
    const d8 = digits.slice(0, 8);
    return `${d8.slice(0, 4)}-${d8.slice(4, 6)}-${d8.slice(6, 8)}`;
  }
  return s.slice(0, 10);
}

function consolidatedUnderlyingKey(row: ScannerData): string {
  return String(row.Underlying || (row as { underlyingSymbol?: string }).underlyingSymbol || '')
    .replace(/^\$/, '')
    .trim()
    .toUpperCase();
}

function consolidatedStrikeKey(row: ScannerData): number {
  const n = Number(row.Strike);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 1000) / 1000;
}

/** Infer missing expiry on one leg from the sibling at the same underlying + strike. */
function buildExpiryInferenceMap(rows: ScannerData[]): Map<string, string> {
  const counts = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const u = consolidatedUnderlyingKey(row);
    const strike = consolidatedStrikeKey(row);
    const exp = normalizeConsolidatedExpiryKey(row);
    if (!u || !Number.isFinite(strike) || !exp) continue;
    const legKey = `${u}|${strike}`;
    let bucket = counts.get(legKey);
    if (!bucket) {
      bucket = new Map();
      counts.set(legKey, bucket);
    }
    bucket.set(exp, (bucket.get(exp) || 0) + 1);
  }
  const out = new Map<string, string>();
  Array.from(counts.entries()).forEach(([legKey, bucket]) => {
    let best = '';
    let bestN = 0;
    Array.from(bucket.entries()).forEach(([exp, n]) => {
      if (n > bestN) {
        bestN = n;
        best = exp;
      }
    });
    if (best) out.set(legKey, best);
  });
  return out;
}

function resolveRowExpiry(row: ScannerData, inference: Map<string, string>): string {
  let exp = normalizeConsolidatedExpiryKey(row);
  if (exp) return exp;
  const u = consolidatedUnderlyingKey(row);
  const strike = consolidatedStrikeKey(row);
  if (!u || !Number.isFinite(strike)) return '';
  return inference.get(`${u}|${strike}`) || '';
}

function formatExpiryDetail(labels: string[]): string {
  const uniq = Array.from(new Set(labels.filter(Boolean))).sort();
  if (uniq.length <= 1) return uniq[0] || '';
  if (uniq.length <= 3) return `${uniq.length} exp: ${uniq.join(', ')}`;
  return `${uniq.length} exp: ${uniq.slice(0, 2).join(', ')} +${uniq.length - 2}`;
}

function buildConsolidatedGroupKey(
  row: ScannerData,
  groupBy: ConsolidatedGroupBy,
  inference: Map<string, string>
): string {
  const u = consolidatedUnderlyingKey(row);
  const strike = consolidatedStrikeKey(row);
  if (!u || !Number.isFinite(strike)) return '';
  if (groupBy === 'strike') return `${u}|${strike}`;
  const exp = resolveRowExpiry(row, inference);
  return `${u}|${exp}|${strike}`;
}

/**
 * Historical / drill-down row for a consolidated net: negative net → put leg, positive → call leg,
 * zero → first available leg (matches Net OI put/call intuition).
 */
function sampleRowForSignedNet(
  net: number,
  sampleCallRow: ScannerData | null,
  samplePutRow: ScannerData | null,
  fallback: ScannerData
): ScannerData {
  if (net < 0) return samplePutRow ?? fallback;
  if (net > 0) return sampleCallRow ?? fallback;
  return sampleCallRow ?? samplePutRow ?? fallback;
}

/**
 * RTL legacy: same rules as legacy volume charts (`legacyVolumeStrikeAggregation`):
 * - Net Δ (directional size): (boughtCalls + soldPuts) − (soldCalls + boughtPuts) on effective bid/ask volumes.
 * - Net trades column: premium flow (boughtCallPrem + soldPutPrem) − (soldCallPrem + boughtPutPrem) with prem = sideVolume × price × 100.
 * - Net volume: total call contracts − total put contracts = (boughtCalls + soldCalls) − (boughtPuts + soldPuts).
 */
export function buildStrikeAggregates(
  rows: ScannerData[],
  mode: ConsolidatedTableMode,
  spot: number,
  rtlDataMode: DataMode = '5min',
  groupBy: ConsolidatedGroupBy = 'strike_expiry'
): StrikeAggRow[] {
  const inference = buildExpiryInferenceMap(rows);
  const groups = new Map<string, ScannerData[]>();
  for (const row of rows) {
    const key = buildConsolidatedGroupKey(row, groupBy, inference);
    if (!key) continue;
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }

  const out: StrikeAggRow[] = [];
  for (const grp of Array.from(groups.values())) {
    if (!grp.length) continue;
    const sampleRow = grp[0];
    const strike = consolidatedStrikeKey(sampleRow);
    const expiryLabels = grp.map((r) => resolveRowExpiry(r, inference)).filter(Boolean);
    const primaryExpiry = expiryLabels[0] || resolveRowExpiry(sampleRow, inference);
    const expiryDetail = groupBy === 'strike' ? formatExpiryDetail(expiryLabels) : primaryExpiry;
    let netDelta = 0;
    let netGamma = 0;
    let netVanna = 0;
    let netCharm = 0;
    let callOI = 0;
    let putOI = 0;
    let netVolume = 0;
    let netTrades = 0;
    let sampleCallRow: ScannerData | null = null;
    let samplePutRow: ScannerData | null = null;

    for (const r of grp) {
      const rec = r as unknown as Record<string, unknown>;
      const oi = num(rec, 'Open Interest', 'open_interest', 'openInterest', 'OI', 'oi');
      const side = optionSide(r);
      if (side === 'C') {
        callOI += oi;
        if (!sampleCallRow) sampleCallRow = r;
      } else {
        putOI += oi;
        if (!samplePutRow) samplePutRow = r;
      }

      if (mode === 'stable') {
        const d = num(rec, 'Delta', 'delta');
        const g = num(rec, 'Gamma', 'gamma');
        const vanna = numGreekFromRowOrNested(rec, 'Vanna', 'vanna', 'VANNA');
        const charm = numGreekFromRowOrNested(rec, 'Charm', 'charm', 'CHARM');
        netDelta += d * oi;
        netGamma += g * oi;
        netVanna += vanna * oi;
        netCharm += charm * oi;
      } else {
        const optionType = parseOptionSide(r);
        if (!optionType) continue;
        const { bid: effBid, ask: effAsk } = computeEffectiveBidAskVolumes(r, rtlDataMode);
        const px = effectiveOptionPriceForPremium(r);
        const boughtPrem = effAsk * px * 100;
        const soldPrem = effBid * px * 100;
        if (optionType === 'CALL') {
          const boughtCalls = effAsk;
          const soldCalls = effBid;
          netDelta += boughtCalls - soldCalls;
          netVolume += boughtCalls + soldCalls;
          netTrades += boughtPrem - soldPrem;
        } else {
          const boughtPuts = effAsk;
          const soldPuts = effBid;
          netDelta += soldPuts - boughtPuts;
          netVolume -= boughtPuts + soldPuts;
          netTrades += soldPrem - boughtPrem;
        }
      }
    }

    const netOI = callOI - putOI;
    const pctFromSpot = spot > 0 ? ((strike - spot) / spot) * 100 : 0;
    out.push({
      underlying: consolidatedUnderlyingKey(sampleRow) || String(sampleRow.Underlying || ''),
      expiry: primaryExpiry,
      expiryDetail,
      strike,
      netDelta,
      netGamma,
      netVanna,
      netCharm,
      netOI,
      callOI,
      putOI,
      pctFromSpot,
      netVolume,
      netTrades,
      sampleRow,
      sampleCallRow,
      samplePutRow,
    });
  }

  return out.sort((a, b) => a.strike - b.strike);
}

/**
 * Bar length scale per column: ~90th percentile of |value| so a few spikes do not shrink every other bar.
 * Values above the cap still render up to full half-width. Direction stays sign(value) vs zero.
 */
function columnRobustBarCap(values: number[]): number {
  const absVals: number[] = [];
  for (const x of values) {
    if (Number.isFinite(x)) absVals.push(Math.abs(x));
  }
  const n = absVals.length;
  if (n === 0) return 1;
  absVals.sort((a, b) => a - b);
  const maxAbs = absVals[n - 1];
  if (maxAbs === 0) return 1;

  const idx = Math.min(n - 1, Math.max(0, Math.floor(0.9 * (n - 1))));
  let cap = absVals[idx];

  if (cap === 0) {
    const pos = absVals.filter((a) => a > 0);
    if (pos.length === 0) return 1;
    cap = pos[Math.min(pos.length - 1, Math.max(0, Math.floor(0.75 * (pos.length - 1))))];
  }

  return Math.max(cap, 1e-12);
}

function fmtCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  if (a >= 100) return n.toFixed(0);
  if (a >= 1) return n.toFixed(2);
  return n.toFixed(4);
}

/** Stable consolidated: readable numbers (grouping / compact tiers) — not squeezed inside the bar track. */
type StableMetricKind = 'greek' | 'oi' | 'pct';

function formatStableMetric(n: number, kind: StableMetricKind): string {
  if (!Number.isFinite(n)) return '—';
  if (kind === 'pct') {
    const s = n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
    return `${s}%`;
  }
  if (kind === 'oi') {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(n));
  }
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e5) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
  if (abs >= 100) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
  if (abs >= 1) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(n);
  return n.toFixed(4);
}

/** Stable mode: number on its own line + thin directional bar (less visual noise than label-on-bar). */
const StableMetricCell: React.FC<{
  value: number;
  barCap: number;
  posColor: string;
  negColor: string;
  track: string;
  centerLine: string;
  textColor: string;
  mutedColor: string;
  fmt: StableMetricKind;
}> = ({ value, barCap, posColor, negColor, track, centerLine, textColor, mutedColor, fmt }) => {
  const span = Math.max(barCap, 1e-12);
  const halfPct = Math.min(49.5, (Math.abs(value) / span) * 49.5);
  const toRight = value >= 0;
  const showBar = Number.isFinite(value) && halfPct > 0.04;
  const barColor = value < 0 ? negColor : posColor;
  const label = formatStableMetric(value, fmt);
  const valColor = !Number.isFinite(value) ? mutedColor : value > 0 ? posColor : value < 0 ? negColor : mutedColor;

  return (
    <div
      dir="ltr"
      style={{
        width: '100%',
        minWidth: 96,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
      }}
    >
      <div
        style={{
          textAlign: 'right',
          fontSize: 12,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum" 1',
          letterSpacing: '0.01em',
          color: valColor,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={label}
      >
        {label}
      </div>
      <div style={{ position: 'relative', height: 5, background: track, borderRadius: 3, overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 1,
            background: centerLine,
            zIndex: 1,
          }}
        />
        {showBar && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: toRight ? '50%' : `${50 - halfPct}%`,
              width: `${halfPct}%`,
              backgroundColor: barColor,
              borderRadius: 2,
              zIndex: 0,
              opacity: 0.88,
            }}
          />
        )}
      </div>
    </div>
  );
};

const BAR_TRACK_MIN_PX = 140;

const BarCell: React.FC<{
  value: number;
  barCap: number;
  posColor: string;
  negColor: string;
  track: string;
  centerLine: string;
  labelColor: string;
}> = ({ value, barCap, posColor, negColor, track, centerLine, labelColor }) => {
  const span = Math.max(barCap, 1e-12);
  const halfPct = Math.min(49.5, (Math.abs(value) / span) * 49.5);
  const toRight = value >= 0;
  const showBar = Number.isFinite(value) && halfPct > 0.04;
  const barCool = value < 0 ? negColor : posColor;
  const fin = Number.isFinite(value);
  const label = fin ? fmtCompact(value) : '—';
  /** Value text sits on top of the track (full width) so large stable numbers are not clipped by fixed side gutters. */
  const labelJustify =
    !fin || value === 0 ? 'center' : value < 0 ? 'flex-start' : 'flex-end';

  return (
    <div dir="ltr" style={{ width: '100%', minWidth: BAR_TRACK_MIN_PX }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          minWidth: BAR_TRACK_MIN_PX,
          height: 20,
          background: track,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 1,
            background: centerLine,
            zIndex: 1,
          }}
        />
        {showBar && (
          <div
            style={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              left: toRight ? '50%' : `${50 - halfPct}%`,
              width: `${halfPct}%`,
              backgroundColor: barCool,
              borderRadius: 3,
              zIndex: 0,
            }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: labelJustify,
            paddingLeft: 6,
            paddingRight: 6,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: labelColor,
              lineHeight: 1.1,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textShadow:
                '0 0 4px rgba(0,0,0,0.55), 0 0 10px rgba(255,255,255,0.35), 0 1px 0 rgba(255,255,255,0.25)',
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

export interface DataTableConsolidatedViewProps {
  rows: ScannerData[];
  mode: ConsolidatedTableMode;
  language: string;
  spot: number;
  /** MMEC theme colors */
  colors: {
    background: string;
    surface: string;
    surfaceBorder: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    primary: string;
    chartPrimary?: string;
    error?: string;
    warning?: string;
  };
  openHistorical: (p: ContractHistoricalParams) => void;
  /** Stable: right-click row → Analytics Hub deep analysis */
  onOpenInAnalyticsHub?: (row: ScannerData, columnKey: string) => void;
  onRowClick?: (underlyingSymbol: string) => void;
  /** RTL consolidated: which bid/ask / delta fields to read (`5min` vs `accumulated`). */
  rtlDataMode?: DataMode;
  /** Roll up: one row per strike+expiry (default) or one row per strike across expiries. */
  groupBy?: ConsolidatedGroupBy;
  onGroupByChange?: (value: ConsolidatedGroupBy) => void;
  /** Simple shell: shrink fonts / table min-width to fit narrow viewports. */
  simpleDensity?: SimpleScannerTableDensity;
}

function openRowHistorical(
  openHistorical: (p: ContractHistoricalParams) => void,
  row: ScannerData,
  columnKey: string
) {
  const expiryRaw = getExpiryFromRecord(row) || (row['Expiry Date'] as string) || '';
  const expiryStr =
    typeof expiryRaw === 'string'
      ? expiryRaw
      : expiryRaw && (expiryRaw as Date).toISOString
        ? (expiryRaw as Date).toISOString().slice(0, 10)
        : '';
  const source = columnKeyToHistoricalSource(columnKey);
  const ot = String(row['Option Type'] ?? '').toUpperCase();
  const optionType: 'CALL' | 'PUT' = ot === 'P' || ot === 'PUT' ? 'PUT' : 'CALL';
  openHistorical({
    underlying: String(row.Underlying || ''),
    strike: (Number(row.Strike) || row.Strike) ?? '',
    expiry: expiryStr.slice(0, 10),
    optionType,
    source,
  });
}

export const DataTableConsolidatedView: React.FC<DataTableConsolidatedViewProps> = ({
  rows,
  mode,
  language,
  spot,
  colors,
  openHistorical,
  onOpenInAnalyticsHub,
  onRowClick,
  rtlDataMode = '5min',
  groupBy = 'strike_expiry',
  onGroupByChange,
  simpleDensity,
}) => {
  const densityMetrics = useMemo(
    () => (simpleDensity ? getSimpleTableDensityMetrics(simpleDensity) : null),
    [simpleDensity],
  );
  const cScale = densityMetrics?.consolidatedTableScale ?? 1;

  const agg = useMemo(
    () => buildStrikeAggregates(rows, mode, spot, rtlDataMode, groupBy),
    [rows, mode, spot, rtlDataMode, groupBy]
  );

  const scaleDelta = useMemo(() => columnRobustBarCap(agg.map((r) => r.netDelta)), [agg]);
  const scaleGamma = useMemo(() => columnRobustBarCap(agg.map((r) => r.netGamma)), [agg]);
  const scaleVanna = useMemo(() => columnRobustBarCap(agg.map((r) => r.netVanna)), [agg]);
  const scaleCharm = useMemo(() => columnRobustBarCap(agg.map((r) => r.netCharm)), [agg]);
  const scaleNetOi = useMemo(() => columnRobustBarCap(agg.map((r) => r.netOI)), [agg]);
  const scaleCallOi = useMemo(() => columnRobustBarCap(agg.map((r) => r.callOI)), [agg]);
  const scalePutOi = useMemo(() => columnRobustBarCap(agg.map((r) => r.putOI)), [agg]);
  const scalePct = useMemo(() => columnRobustBarCap(agg.map((r) => r.pctFromSpot)), [agg]);
  const scaleVol = useMemo(() => columnRobustBarCap(agg.map((r) => r.netVolume)), [agg]);
  const scaleTrades = useMemo(() => columnRobustBarCap(agg.map((r) => r.netTrades)), [agg]);

  const atmStrike = useMemo(() => {
    if (!agg.length || !Number.isFinite(spot) || spot <= 0) return null;
    let best = agg[0];
    let bestDist = Math.abs(best.strike - spot);
    for (const r of agg) {
      const d = Math.abs(r.strike - spot);
      if (d < bestDist) {
        bestDist = d;
        best = r;
      }
    }
    return best.strike;
  }, [agg, spot]);

  const cyan = colors.chartPrimary || '#00d4ff';
  const magenta = colors.error || '#ff00c8';
  const orange = colors.warning || '#d48806';
  const track = 'color-mix(in srgb, var(--muted) 12%, var(--elev))';
  const centerLine = 'color-mix(in srgb, var(--muted) 55%, transparent)';

  const onCell = useCallback(
    (sample: ScannerData | null, columnKey: string) => {
      if (!sample) return;
      if (onRowClick) {
        const u = String(sample.Underlying || '').replace(/^\$/, '').trim();
        if (u) onRowClick(u);
        return;
      }
      openRowHistorical(openHistorical, sample, columnKey);
    },
    [openHistorical, onRowClick]
  );

  const headerFs = Math.max(9, Math.round(11 * (simpleDensity === 'dense' ? 0.88 : simpleDensity === 'compact' ? 0.95 : 1)));
  const cellFs = Math.max(9, Math.round(12.5 * (simpleDensity === 'dense' ? 0.88 : simpleDensity === 'compact' ? 0.95 : 1)));
  const thPadY = simpleDensity === 'dense' ? 4 : simpleDensity === 'compact' ? 5 : 6;
  const thPadX = simpleDensity === 'dense' ? 6 : simpleDensity === 'compact' ? 8 : 10;
  const tdPadY = simpleDensity === 'dense' ? 4 : simpleDensity === 'compact' ? 5 : 6;
  const tdPadX = simpleDensity === 'dense' ? 6 : simpleDensity === 'compact' ? 8 : 10;

  const thStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    padding: `${thPadY}px ${thPadX}px`,
    textAlign: 'end',
    fontSize: headerFs,
    fontWeight: 600,
    letterSpacing: '0.03em',
    color: 'var(--muted)',
    borderBottom: '1px solid var(--hair)',
    backgroundColor: 'var(--elev)',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: `${tdPadY}px ${tdPadX}px`,
    fontSize: cellFs,
    color: 'var(--txt)',
    borderBottom: '1px solid var(--hair)',
    verticalAlign: 'middle',
    fontVariantNumeric: 'tabular-nums',
    cursor: onRowClick ? 'pointer' : 'pointer',
  };

  /** Bar columns need a floor width so % widths are visible (otherwise flex shrinks to hairlines). */
  const tdBarStyle: React.CSSProperties = {
    ...tdStyle,
    minWidth: Math.max(52, Math.round((BAR_TRACK_MIN_PX + 24) * cScale)),
    width: '11%',
    maxWidth: Math.round(360 * cScale),
  };

  const tdBarStyleStable: React.CSSProperties = {
    ...tdStyle,
    minWidth: Math.max(72, Math.round(108 * cScale)),
    width: '10%',
    maxWidth: Math.round(280 * cScale),
  };

  const stableCols = mode === 'stable';

  const groupByControl = onGroupByChange ? (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderBottom: '1px solid var(--hair)',
        fontSize: cellFs,
        color: 'var(--txt-2)',
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--txt)' }}>
        {language === 'ar' ? 'تجميع حسب' : 'Group by'}
      </span>
      <select
        value={groupBy}
        onChange={(e) => onGroupByChange(e.target.value as ConsolidatedGroupBy)}
        style={{
          borderRadius: 6,
          border: '1px solid var(--hair)',
          background: 'var(--base)',
          color: 'var(--txt)',
          fontSize: cellFs,
          fontFamily: 'var(--font-ui)',
          padding: '4px 8px',
        }}
        aria-label={language === 'ar' ? 'طريقة التجميع' : 'Consolidated grouping'}
      >
        <option value="strike_expiry">
          {language === 'ar' ? 'سعر التنفيذ + انتهاء' : 'Strike + expiry'}
        </option>
        <option value="strike">
          {language === 'ar' ? 'سعر التنفيذ فقط (كل الانتهاءات)' : 'Strike only (all expiries)'}
        </option>
      </select>
      <span style={{ fontSize: Math.max(9, cellFs - 1), color: 'var(--muted)' }}>
        {language === 'ar'
          ? 'صف واحد لكل سعر التنفيذ: يدمج خيار الشراء وخيار البيع. اختر «سعر التنفيذ فقط» لدمج عدة تواريخ انتهاء.'
          : 'One row per strike merges calls & puts. Use Strike only to roll up multiple expiries.'}
      </span>
    </div>
  ) : null;

  return (
    <div
      className="border"
      style={{
        borderColor: 'var(--hair)',
        backgroundColor: 'var(--elev)',
        borderRadius: 10,
        fontFamily: 'var(--font-ui)',
      }}
    >
      {groupByControl}
      <table
        className="w-full border-collapse"
        style={{
          minWidth: Math.round((stableCols ? 1080 : 1280) * cScale),
          tableLayout: 'fixed',
          width: '100%',
        }}
      >
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '9%', minWidth: Math.max(56, Math.round(72 * cScale)) }}>
              {language === 'ar' ? 'سعر التنفيذ' : 'Strike'}
            </th>
            {(stableCols || groupBy === 'strike' || mode === 'rtl_legacy') && (
              <th style={{ ...thStyle, textAlign: 'start', width: '11%', minWidth: Math.max(72, Math.round(88 * cScale)) }}>
                {language === 'ar' ? 'انتهاء' : 'Expiry'}
              </th>
            )}
            {stableCols ? (
              <>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'صافي دلتا' : 'Net Δ'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'صافي جاما' : 'Net Γ'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'صافي فانا' : 'Net Vanna'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'صافي تشارم' : 'Net Charm'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'صافي OI' : 'Net OI'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'كال OI' : 'Call OI'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? 'خيار بيع OI' : 'Put OI'}
                </th>
                <th style={{ ...thStyle, minWidth: Math.max(72, Math.round(108 * cScale)), width: '10%' }}>
                  {language === 'ar' ? '% من السخيار البيع' : '% from spot'}
                </th>
              </>
            ) : (
              <>
                <th style={{ ...thStyle, minWidth: tdBarStyle.minWidth as number, width: '14%' }}>{language === 'ar' ? 'صافي دلتا' : 'Net Δ'}</th>
                <th style={{ ...thStyle, minWidth: tdBarStyle.minWidth as number, width: '14%' }}>{language === 'ar' ? 'صافي حجم' : 'Net volume'}</th>
                <th style={{ ...thStyle, minWidth: tdBarStyle.minWidth as number, width: '14%' }}>{language === 'ar' ? 'صافي صفقات' : 'Net trades'}</th>
                <th style={{ ...thStyle, minWidth: tdBarStyle.minWidth as number, width: '14%' }}>{language === 'ar' ? '% من السخيار البيع' : '% from spot'}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {agg.map((r) => {
            const isAtm = atmStrike !== null && r.strike === atmStrike;
            const rowBg = isAtm ? `color-mix(in srgb, ${orange} 14%, ${colors.background})` : undefined;
            const rowBorder = isAtm ? `2px solid ${orange}` : undefined;
            return (
              <tr
                key={`${r.underlying}-${r.expiry}-${r.strike}-${r.expiryDetail}`}
                className="hover:bg-[var(--mmec-row-hover)]"
                style={{ backgroundColor: rowBg, outline: rowBorder, outlineOffset: -1 }}
                onContextMenu={(e) => {
                  if (!onOpenInAnalyticsHub || !r.sampleRow) return;
                  e.preventDefault();
                  onOpenInAnalyticsHub(r.sampleRow, 'Strike');
                }}
              >
                <td
                  style={{
                    ...tdStyle,
                    width: '9%',
                    minWidth: Math.max(56, Math.round(72 * cScale)),
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                    textAlign: 'end',
                  }}
                  onClick={() => onCell(r.sampleRow, 'Strike')}
                >
                  {r.strike}
                </td>
                {(stableCols || groupBy === 'strike' || mode === 'rtl_legacy') && (
                  <td
                    style={{
                      ...tdStyle,
                      width: '11%',
                      minWidth: Math.max(72, Math.round(88 * cScale)),
                      fontSize: Math.max(9, cellFs - 1),
                      color: 'var(--txt-2)',
                      fontFamily: 'var(--font-mono)',
                      lineHeight: 1.25,
                    }}
                    title={r.expiryDetail || r.expiry}
                  >
                    {groupBy === 'strike' ? (r.expiryDetail || r.expiry || '—') : (r.expiry || '—')}
                  </td>
                )}
                {stableCols ? (
                  <>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(sampleRowForSignedNet(r.netDelta, r.sampleCallRow, r.samplePutRow, r.sampleRow), 'Delta')
                      }
                    >
                      <StableMetricCell
                        value={r.netDelta}
                        barCap={scaleDelta}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="greek"
                      />
                    </td>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(sampleRowForSignedNet(r.netGamma, r.sampleCallRow, r.samplePutRow, r.sampleRow), 'Gamma')
                      }
                    >
                      <StableMetricCell
                        value={r.netGamma}
                        barCap={scaleGamma}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="greek"
                      />
                    </td>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(sampleRowForSignedNet(r.netVanna, r.sampleCallRow, r.samplePutRow, r.sampleRow), 'Vanna')
                      }
                    >
                      <StableMetricCell
                        value={r.netVanna}
                        barCap={scaleVanna}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="greek"
                      />
                    </td>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(sampleRowForSignedNet(r.netCharm, r.sampleCallRow, r.samplePutRow, r.sampleRow), 'Charm')
                      }
                    >
                      <StableMetricCell
                        value={r.netCharm}
                        barCap={scaleCharm}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="greek"
                      />
                    </td>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.netOI, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Open Interest'
                        )
                      }
                    >
                      <StableMetricCell
                        value={r.netOI}
                        barCap={scaleNetOi}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="oi"
                      />
                    </td>
                    <td style={tdBarStyleStable} onClick={() => onCell(r.sampleCallRow || r.sampleRow, 'Open Interest')}>
                      <StableMetricCell
                        value={r.callOI}
                        barCap={scaleCallOi}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="oi"
                      />
                    </td>
                    <td style={tdBarStyleStable} onClick={() => onCell(r.samplePutRow || r.sampleRow, 'Open Interest')}>
                      <StableMetricCell
                        value={r.putOI}
                        barCap={scalePutOi}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="oi"
                      />
                    </td>
                    <td
                      style={tdBarStyleStable}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.pctFromSpot, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Mid'
                        )
                      }
                    >
                      <StableMetricCell
                        value={r.pctFromSpot}
                        barCap={scalePct}
                        posColor={cyan}
                        negColor={magenta}
                        track={track}
                        centerLine={centerLine}
                        textColor={colors.text}
                        mutedColor={colors.textMuted}
                        fmt="pct"
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td
                      style={tdBarStyle}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.netDelta, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Delta ( ask - bid )'
                        )
                      }
                    >
                      <BarCell value={r.netDelta} barCap={scaleDelta} posColor={cyan} negColor={magenta} track={track} centerLine={centerLine} labelColor={colors.text} />
                    </td>
                    <td
                      style={tdBarStyle}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.netVolume, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Volume'
                        )
                      }
                    >
                      <BarCell value={r.netVolume} barCap={scaleVol} posColor={cyan} negColor={magenta} track={track} centerLine={centerLine} labelColor={colors.text} />
                    </td>
                    <td
                      style={tdBarStyle}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.netTrades, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Trade Count'
                        )
                      }
                    >
                      <BarCell value={r.netTrades} barCap={scaleTrades} posColor={cyan} negColor={magenta} track={track} centerLine={centerLine} labelColor={colors.text} />
                    </td>
                    <td
                      style={tdBarStyle}
                      onClick={() =>
                        onCell(
                          sampleRowForSignedNet(r.pctFromSpot, r.sampleCallRow, r.samplePutRow, r.sampleRow),
                          'Mid'
                        )
                      }
                    >
                      <BarCell value={r.pctFromSpot} barCap={scalePct} posColor={cyan} negColor={magenta} track={track} centerLine={centerLine} labelColor={colors.text} />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {agg.length === 0 && (
        <div className="p-6 text-center" style={{ color: 'var(--muted)', fontSize: 13 }}>
          {language === 'ar' ? 'لا توجد بيانات للعرض المُجمَّع.' : 'No rows to show in consolidated view.'}
        </div>
      )}
    </div>
  );
};

export default DataTableConsolidatedView;
